import type { DrokMyCopy } from "@rakazo/contracts";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { useQueryUserId } from "../lib/query";
import { drokRpc } from "./rpc";
import { useDrokAccess } from "./use-drok-access";

/** Keyed by user, so signing in as someone else loads again. */
export function drokCopiesKey(userId: string | null) {
  return ["drok", `user:${userId ?? ""}`, "copies.mine"] as const;
}

export type DrokCopiesState = {
  /** The caller's copies; undefined while loading, after a failed load, and for IT. */
  copies: DrokMyCopy[] | undefined;
  /** IT, or enforcement is off: nothing is restricted. */
  unrestricted: boolean;
};

/**
 * The caller's copies and what each allows, loaded only for employees. Refetched on window focus
 * and when `botListKey` changes (the bot list changed, so a copy may have been pushed or removed).
 * A failed load leaves `copies` undefined, which every consumer treats as locked.
 */
export function useDrokCopies(botListKey?: string): DrokCopiesState {
  const userId = useQueryUserId();
  const access = useDrokAccess();
  const employee = access === "employee" && userId !== null;
  const query = useQuery({
    queryKey: drokCopiesKey(userId),
    queryFn: () => drokRpc.copies.mine(),
    enabled: employee,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });
  const { refetch } = query;
  const lastKey = useRef(botListKey);
  useEffect(() => {
    if (lastKey.current === botListKey) return;
    lastKey.current = botListKey;
    if (employee) void refetch();
  }, [botListKey, employee, refetch]);
  return {
    copies: employee && !query.isError ? query.data : undefined,
    unrestricted: access === "manage-agents",
  };
}
